import { addMonths, eachDayOfInterval, subDays, isToday } from "date-fns";
import { useCallback, useMemo } from "react";
import { cn, formatWeekDay } from "../../../../lib/utils";
import { EventFragment } from "../../../../__generated__/graphql";
import EventItem from "../EventItem";
import { useCalendar } from "../../context/hook";
import { motion } from "motion/react";
import FullLoading from "../../../FullLoading";

export default function LinearCalendar() {
  const { events, loading, error, datesInPeriod } = useCalendar();

  const eventsByDate = useMemo(() => {
    if (!events) return new Map<string, EventFragment[]>();

    return events.reduce((map, event) => {
      if (!event) return map;
      const eventDate = new Date(event.startDate);
      const date = eventDate.toLocaleDateString("fr-FR");
      const events = map.get(date) || [];
      events.push(event);
      map.set(date, events);
      return map;
    }, new Map<string, EventFragment[]>());
  }, [events]);

  const monthEvents = useCallback(
    (month: Date) => {
      const daysListForMonth = eachDayOfInterval({
        start: month,
        end: subDays(addMonths(month, 1), 1),
      });

      return daysListForMonth.map((day) => {
        const dateKey = day.toLocaleDateString("fr-FR");
        const eventsForDay = eventsByDate?.get(dateKey) || [];

        return {
          date: day,
          events: eventsForDay,
        };
      });
    },
    [eventsByDate]
  );

  const memoizedMonthEvents = useMemo(() => {
    return datesInPeriod.reduce((acc, date) => {
      acc[date.date.toISOString()] = monthEvents(date.date);
      return acc;
    }, {} as Record<string, ReturnType<typeof monthEvents>>);
  }, [datesInPeriod, monthEvents]);

  if (loading) return <FullLoading />;

  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      <motion.div
        className="flex gap-4 pb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {datesInPeriod.map((month) => (
          <div key={month.date.toISOString()} className="flex-1">
            <div className="font-semibold mb-2">{month.formatted}</div>
            <div className="border border-border rounded-md">
              {memoizedMonthEvents[month.date.toISOString()].map(
                (day, index) => {
                  const isFirstDay = index === 0;
                  const isLastDay =
                    index ===
                    memoizedMonthEvents[month.date.toISOString()].length - 1;

                  return (
                    <div
                      key={day.date.toISOString()}
                      className={cn(
                        "grid grid-cols-[auto_1fr] gap-0",
                        !isLastDay && "border-b border-input"
                      )}
                    >
                      <div
                        className={cn(
                          " flex items-start text-secondary bg-dark w-22 p-2",
                          isFirstDay && "rounded-tl-md",
                          isLastDay && "rounded-bl-md",
                          isToday(day.date) &&
                            "text-accent border-l-2 border-l-accent"
                        )}
                      >
                        {formatWeekDay(day.date)}
                      </div>

                      <div className="flex flex-col gap-1 p-2">
                        {day.events?.map((event) => {
                          if (!event) return null;

                          return <EventItem key={event.id} event={event} />;
                        })}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        ))}
      </motion.div>
    </>
  );
}
